// src/components/LoginModal.jsx
import React, { useState, useEffect } from "react"; // Added useEffect
import { X, Eye, EyeOff } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { auth, googleProvider, signInWithEmailAndPassword, sendPasswordResetEmail } from "./firebase";
import { signInWithPopup } from "firebase/auth";
import { useUser } from "./UserContext";
import { createUserData } from "./utils/userData"; // ✅ añadimos esta importación

const LoginModal = ({ isOpen, onClose, onSwitchToSignup }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false); // Added loading state
  const [apiError, setApiError] = useState(null); // Added apiError state
  const [resetSent, setResetSent] = useState(false); // New state for reset success message
  const { setUser } = useUser();

  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setPassword("");
      setErrors({});
      setApiError(null);
      setLoading(false);
      setResetSent(false); // Clear reset status
    }
  }, [isOpen]); // Run when isOpen changes

  if (!isOpen) return null;

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setErrors({ email: "Please enter your email to reset password." });
      return;
    }
    setLoading(true);
    setApiError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setErrors({});
    } catch (error) {
      console.error("Error sending password reset email:", error);
      setApiError("Failed to send reset email. Make sure the email is correct.");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      newErrors.email = "Invalid email format.";
    }

    if (!password.trim()) {
      newErrors.password = "Password is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => { // Made async
    e.preventDefault();
    setApiError(null); // Clear previous errors
    if (validate()) {
      setLoading(true); // Start loading
      try {
        await signInWithEmailAndPassword(auth, email, password);
        // UserContext will automatically pick up the logged-in user
        setApiError(null);
        onClose(); // Close modal on successful login
      } catch (error) {
        console.error("Error logging in:", error.code, error.message);
        let errorMessage = "Failed to log in. Please try again.";
        if (error.code === "auth/user-not-found" || error.code === "auth/wrong-password") {
          errorMessage = "Invalid email or password.";
        } else if (error.code === "auth/invalid-email") {
          errorMessage = "Invalid email address format.";
        }
        setApiError(errorMessage);
      } finally {
        setLoading(false); // End loading
      }
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true); // Start loading for Google login
    setApiError(null); // Clear previous errors
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      await createUserData(user); // Create user document if it doesn't exist
      // setUser(user); // UserContext will automatically pick up the logged-in user
      onClose(); // Close modal
      console.log("Usuario logueado con Google:", user);
    } catch (error) {
      if (error.code === "auth/popup-closed-by-user") {
        console.log("El usuario cerró el popup de Google sin completar el login.");
      } else {
        console.error("Error al iniciar sesión con Google:", error);
        setApiError("Failed to log in with Google. Please try again.");
      }
    } finally {
      setLoading(false); // End loading
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-center items-center px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-black">
          <X />
        </button>

        <h2 className="text-3xl font-bold text-gray-900 mb-2">Login</h2>
        <p className="text-sm text-gray-500 mb-6">
          Don’t have an account?{" "}
          <span
            className="text-orange-500 font-semibold cursor-pointer"
            onClick={onSwitchToSignup}
          >
            Sign Up
          </span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              placeholder="email@address.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full px-4 py-3 bg-gray-100 rounded-xl text-black outline-none ${
                errors.email ? "border border-red-500" : ""
              }`}
              disabled={loading} // Disabled when loading
              autoComplete="email"
            />
            {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 bg-gray-100 rounded-xl text-black outline-none pr-10 ${
                  errors.password ? "border border-red-500" : ""
                }`}
                disabled={loading} // Disabled when loading
                autoComplete="current-password"
              />
              <span
                className="absolute top-3 right-3 cursor-pointer text-gray-400"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </span>
            </div>
            {errors.password && <p className="text-sm text-red-500 mt-1">{errors.password}</p>}
          </div>

          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-gray-600">
              <input type="checkbox" disabled={loading} />
              Remember me
            </label>
            <button 
              className="text-blue-600 font-semibold" 
              type="button" 
              disabled={loading}
              onClick={handleForgotPassword}
            >
              Forgot Password?
            </button>
          </div>

          {resetSent && (
            <p className="text-sm text-green-600 text-center mt-2 font-medium">
              Password reset link sent to your email!
            </p>
          )}

          {apiError && <p className="text-sm text-red-500 text-center mt-2">{apiError}</p>}

          <button
            type="submit"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl shadow-lg transition-all"
            disabled={loading} // Disabled when loading
          >
            {loading ? "Logging In..." : "Submit"} {/* Loading text */}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          Or login with social media
        </div>

        <div className="flex justify-center mt-4">
          <button
            onClick={handleGoogleLogin}
            type="button"
            className="rounded-full bg-white shadow-lg p-3 hover:shadow-xl transition flex items-center justify-center"
            disabled={loading}
          >
            <FcGoogle size={32} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
